import React, { FC, memo, useEffect } from 'react';
import { Colors } from 'core/CssVariables';
import ToolTip from 'components/ToolTip/ToolTip';
import { useGAEvent } from 'hooks/GoogleAnalytics';
import {
  TGaugeMetaDataUi,
  GaugesEnum,
  TooltipTitlesEnum,
} from 'data/types/gauges.types';
import { tooltipTitleMapper } from 'data/converters/gauges.converter';
import { useAppSelector } from 'redux/store';
import { useTheme } from 'styled-components';
import { TThemeProps } from 'theme/theme';
import {
  SGaugeBar,
  SGaugeBlock,
  SGaugeCollapsedRow,
  SGaugeFigure,
  SGaugeLabel,
  SGaugeSegment,
  SGaugeSub,
  SGaugeValue,
} from '../Gauges.style';
import GaugeTooltipContent from './GaugeTooltipContent';
import GaugeIcon from './GaugeIcon';

export type TGaugeSegment = {
  label: string;
  value?: string | number;
  color?: Colors;
  onClick?: () => void;
  /* Each half explains itself on hover with its own gauge's metadata — New
     carries FR SLA Failed, which is why the merge must not swallow it. */
  tooltipData?: TGaugeMetaDataUi[];
};

export type TGaugeItemProps = {
  tooltipData: TGaugeMetaDataUi[];
  backgroundColor?: Colors;
  onClick?: () => void;
  type: GaugesEnum;
  name: string;
  description: string;
  fitOneRow?: boolean;
  forceCollapsed?: boolean;
  /* Phrase the design prints after the figure, bound to the metric it names. */
  subLabel?: { forKey: TooltipTitlesEnum; text: string };
  /* Present only on the merged ACTIVE TICKETS gauge: each half keeps its own
     drill-down and severity colour. */
  segments?: TGaugeSegment[];
};

/* The gauge colour arrives from the backend as a raw palette value. Translate it
   into the dashbar's own semantic tokens so severity keeps its meaning on both
   the light and the dark surface (the raw values are tuned for light only). */
const severityColor = (
  theme: TThemeProps,
  backgroundColor?: Colors,
): string | undefined => {
  switch (backgroundColor) {
    case Colors.ErrorColor:
      return theme.dashbar.criticalColor;
    case Colors.WarningColor:
      return theme.dashbar.warningColor;
    case Colors.SuccessColor:
      return theme.dashbar.successColor;
    default:
      return theme.dashbar.infoColor;
  }
};

const GaugeItem: FC<TGaugeItemProps> = ({
  tooltipData,
  backgroundColor,
  onClick,
  type,
  name,
  description,
  fitOneRow,
  forceCollapsed,
  subLabel,
  segments,
}) => {
  const isHeaderCollapsed = useAppSelector(
    state => state.appSettings.header.collapsed,
  );
  const collapsed = forceCollapsed || isHeaderCollapsed;
  const { sendGAEvent } = useGAEvent();
  const theme = useTheme() as TThemeProps;
  const barColor = severityColor(theme, backgroundColor);
  /* Only a gauge that actually needs attention tints its figure; the rest stay
     plain so the coloured ones carry weight. The bar always shows severity. */
  const needsAttention =
    backgroundColor === Colors.ErrorColor ||
    backgroundColor === Colors.WarningColor;
  const valueColor = needsAttention ? barColor : undefined;

  useEffect(() => {
    if (
      (type === GaugesEnum.OVERDUE_TICKETS ||
        type === GaugesEnum.OVERDUE_TASKS) &&
      backgroundColor === Colors.ErrorColor
    ) {
      const valueOfGauge =
        tooltipData.find(
          s =>
            s.title ===
            (tooltipTitleMapper[TooltipTitlesEnum.overdueTicketsMoreThanHour]
              .collapsed ||
              tooltipTitleMapper[TooltipTitlesEnum.overdueTasksMoreThanHour]
                .collapsed),
        )?.value || 0;
      sendGAEvent('Gauges', type, valueOfGauge, true);
    }
  }, [backgroundColor]);

  const [primary, ...rest] = tooltipData;

  /* The design prints the figure first, then a phrase naming it: "1 Close to
     fail". The gauge's own phrase is used only when the figure really is the
     metric that phrase names — otherwise it would label whatever the backend
     happened to send first, so the metric's own title is used instead.

     Every remaining metric still follows it. Naming only the headline would
     drop the rest: in the alert state Overdue Tickets carries both a total and
     a ">1hr" count, and that second number is the one that turned the gauge red
     and the one the GA event reports. Stage shows every metric, so we do too. */
  const namesPrimary =
    !!subLabel &&
    primary?.title === tooltipTitleMapper[subLabel.forKey].collapsed;
  const primaryLabel = namesPrimary
    ? (subLabel as { text: string }).text
    : primary?.expanedTitle;
  const figureSubLabel = [
    primaryLabel,
    ...rest.map(el => `${el.expanedTitle}: ${el.value}`),
  ]
    .filter(Boolean)
    .join(' • ');

  /* Collapsed, the merged ACTIVE TICKETS tile is just an icon and a figure —
     no room for the two separate targets the expanded tile offers, so the whole
     tile drills into Open Tickets and the tooltip carries both gauges' detail. */
  const collapsedClick = onClick || segments?.[0]?.onClick;

  if (collapsed) {
    return (
      <ToolTip title={<GaugeTooltipContent tooltipData={tooltipData} />}>
        <SGaugeBlock $clickable={!!collapsedClick} onClick={collapsedClick}>
          <SGaugeCollapsedRow>
            <GaugeIcon type={type} />
            <SGaugeValue $color={valueColor}>{primary?.value}</SGaugeValue>
          </SGaugeCollapsedRow>
          <SGaugeBar $color={barColor}>
            <i />
          </SGaugeBar>
        </SGaugeBlock>
      </ToolTip>
    );
  }

  return (
    <SGaugeBlock
      $fitOneRow={fitOneRow}
      $clickable={!!onClick}
      onClick={onClick}
    >
      <SGaugeLabel>{name}</SGaugeLabel>
      <SGaugeFigure>
        {segments ? (
          segments.map((segment, index) => (
            <React.Fragment key={segment.label}>
              {index > 0 && <SGaugeSub aria-hidden>•</SGaugeSub>}
              <ToolTip
                title={
                  segment.tooltipData ? (
                    <GaugeTooltipContent tooltipData={segment.tooltipData} />
                  ) : (
                    description
                  )
                }
              >
                <SGaugeSegment
                  $clickable={!!segment.onClick}
                  onClick={segment.onClick}
                >
                  <SGaugeValue $color={severityColor(theme, segment.color)}>
                    {segment.value}
                  </SGaugeValue>
                  <SGaugeSub>{segment.label}</SGaugeSub>
                </SGaugeSegment>
              </ToolTip>
            </React.Fragment>
          ))
        ) : (
          <ToolTip title={description}>
            <SGaugeSegment>
              <SGaugeValue $color={valueColor}>{primary?.value}</SGaugeValue>
              {!!figureSubLabel && <SGaugeSub>{figureSubLabel}</SGaugeSub>}
            </SGaugeSegment>
          </ToolTip>
        )}
      </SGaugeFigure>
      <SGaugeBar $color={barColor}>
        <i />
      </SGaugeBar>
    </SGaugeBlock>
  );
};

export default memo(GaugeItem);
